// frontend/src/components/AccessForm.jsx

import { useEffect, useRef, useState } from 'react';
import { verifyAccessByHash } from '../services/hashService';
import { setStoredSession } from '../hooks/useHashSession';
import { useNavigate } from 'react-router-dom';

// Importar hook de traducción
import { useTranslation } from '../i18n';

export default function AccessForm({ prefilledHash = '', prefilledIdentifier = '', onAccess }) {
  const [hash, setHash] = useState(prefilledHash || '');
  const [identifier, setIdentifier] = useState(prefilledIdentifier || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { _ } = useTranslation();
  const autoSubmittedRef = useRef(false);

  const submitAccess = async (rawHash, rawIdentifier) => {
    setError('');
    const h = (rawHash || '').trim();
    const id = (rawIdentifier || '').trim();
    if (!h) {
      setError(_('Please enter your access code.'));
      return;
    }
    if (!id) {
      setError(_('Please enter your email or phone.'));
      return;
    }
    setLoading(true);

    try {
      const data = await verifyAccessByHash(h, id, _);
      const { role, group_name, owner_name, state } = data;

      setStoredSession(
        h,
        role,
        null,
        group_name || null,
        owner_name || null,
        state || null
      );

      if (typeof onAccess === 'function') {
        onAccess();
      }

      if (role === 'leader') {
        navigate('/invitations');
      } else {
        navigate('/members');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 sincronizar si el hash llega después del mount
  useEffect(() => {
    if (prefilledHash) {
      setHash(prefilledHash);
    }
  }, [prefilledHash]);

  useEffect(() => {
    if (prefilledIdentifier) {
      setIdentifier(prefilledIdentifier);
    }
  }, [prefilledIdentifier]);

  // 🔹 auto-submit si viene desde enlace completo
  useEffect(() => {
    const h = (prefilledHash || '').trim();
    const id = (prefilledIdentifier || '').trim();

    if (!h || !id || autoSubmittedRef.current) {
      return;
    }

    autoSubmittedRef.current = true;
    submitAccess(h, id);
  }, [prefilledHash, prefilledIdentifier]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitAccess(hash, identifier);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col items-center gap-4 px-4 py-6"
    >
      <input
        id="hash"
        value={hash}
        onChange={(e) => {
          setHash(e.target.value);
          if (error) setError('');
        }}
        placeholder={_('Enter access code')}
        required
        className="input-access"
      />

      <input
        id="identifier"
        value={identifier}
        onChange={(e) => {
          setIdentifier(e.target.value);
          if (error) setError('');
        }}
        placeholder={_('Your email or phone')}
        required
        className="input-access"
      />

      <button
        type="submit"
        className="button-primary"
        disabled={loading}
      >
        {loading ? _('Validating...') : _('Enter')}
      </button>

      {error && (
        <p className="error-text">{error}</p>
      )}

      <div className="mt-4 text-center text-sm text-gray-500">
        {_('Have an account?')}{' '}
        <a
          href="/login"
          className="ml-1 text-blue-500 hover:underline
           focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
        >
          {_('Sign in here')}
        </a>
      </div>
    </form>
  );
}